import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import supportTicketsService from '../../services/supportTicketsService';
import CreateTicketModal from './CreateTicketModal';
import TicketDetailsModal from './TicketDetailsModal';
import './Dashboard.css';

const TICKET_STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed'];
const TICKET_TYPES = ['Bug', 'Feature', 'Improvement', 'Question'];
const PRIORITY_LEVELS = ['Low', 'Medium', 'High'];

const PRIORITY_CONFIG = {
  High: { color: '#ef4444', bg: '#fef2f2', label: 'High' },
  Medium: { color: '#f59e0b', bg: '#fffbeb', label: 'Medium' },
  Low: { color: '#3b82f6', bg: '#eff6ff', label: 'Low' }
};

const STATUS_CONFIG = {
  'Open': { color: '#22c55e', icon: '🟢' },
  'In Progress': { color: '#f59e0b', icon: '🟡' },
  'Resolved': { color: '#3b82f6', icon: '🔵' },
  'Closed': { color: '#6b7280', icon: '⚫' }
};

const TYPE_CONFIG = {
  'Bug': { color: '#ef4444', bg: '#fef2f2', icon: '🐛' },
  'Feature': { color: '#22c55e', bg: '#f0fdf4', icon: '✨' },
  'Improvement': { color: '#3b82f6', bg: '#eff6ff', icon: '💡' },
  'Question': { color: '#a855f7', bg: '#faf5ff', icon: '❓' }
};

const SOURCE_CONFIG = {
  internal: { color: '#6b7280', bg: '#f9fafb', label: 'Internal', icon: '🧑‍💼' },
  checkout_error: { color: '#dc2626', bg: '#fef2f2', label: 'Checkout Error', icon: '🛒' }
};

const Dashboard = () => {
  const { hasPermission, hasRole } = useAuth();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterStore, setFilterStore] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Mobile filters toggle
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Check permissions
  const canWrite = hasPermission('support-tickets:write') || hasRole('admin');
  const canAdmin = hasPermission('support-tickets:admin') || hasRole('admin');

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.ticket_type = filterType;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterSource !== 'all') params.source = filterSource;
      if (filterStore !== 'all') params.store_slug = filterStore;
      if (searchTerm) params.search = searchTerm;

      const response = await supportTicketsService.getTickets(params);
      setTickets(response.tickets || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err.response?.data?.message || 'Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterPriority, filterSource, filterStore, searchTerm]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await supportTicketsService.getStats();
      setStats(response.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [fetchTickets, fetchStats]);

  // Handle ticket param from URL (for email links)
  useEffect(() => {
    const ticketId = searchParams.get('ticket');
    if (ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.id === parseInt(ticketId));
      if (ticket) {
        setSelectedTicket(ticket);
        setIsDetailsModalOpen(true);
      }
    }
  }, [searchParams, tickets]);

  // Handle ticket click
  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsDetailsModalOpen(true);
  };

  // Handle modal success
  const handleModalSuccess = () => {
    fetchTickets();
    fetchStats();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  // Render priority badge
  const renderPriorityBadge = (priority) => {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.Medium;
    return (
      <span
        className="st-priority-badge"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          borderColor: config.color
        }}
      >
        {config.label}
      </span>
    );
  };

  // Render status indicator
  const renderStatusIndicator = (status) => {
    const config = STATUS_CONFIG[status] || { color: '#6b7280', icon: '•' };
    return (
      <div className="st-status-indicator-wrapper">
        <span
          className="st-status-dot"
          style={{ backgroundColor: config.color }}
        />
        <span className="st-status-label">{status}</span>
      </div>
    );
  };

  // Render type badge
  const renderTypeBadge = (type) => {
    const config = TYPE_CONFIG[type] || TYPE_CONFIG.Bug;
    return (
      <span
        className="st-type-badge"
        style={{
          backgroundColor: config.bg,
          color: config.color
        }}
      >
        {config.icon} {type}
      </span>
    );
  };

  // Render source/store badge for checkout-error tickets
  const renderSourceBadge = (ticket) => {
    if (ticket.source !== 'checkout_error') return null;
    const config = SOURCE_CONFIG.checkout_error;
    return (
      <span
        className="st-source-badge"
        style={{ backgroundColor: config.bg, color: config.color }}
        title={`Reported by customer at checkout${ticket.store_slug ? ` — ${ticket.store_slug}` : ''}`}
      >
        {config.icon} {ticket.store_slug || 'general'}
      </span>
    );
  };

  const storeOptions = Object.keys(stats?.byStore || {});

  // Stat card component
  const StatCard = ({ title, value, icon, color }) => (
    <div className="st-stat-card" style={{ '--accent-color': color }}>
      <div className="st-stat-icon" style={{ backgroundColor: `${color}15`, color }}>
        {icon}
      </div>
      <div className="st-stat-content">
        <span className="st-stat-label">{title}</span>
        <span className="st-stat-value" style={{ color }}>{value}</span>
      </div>
    </div>
  );

  return (
    <div className="st-dashboard">
      {/* Page Header */}
      <div className="st-page-header">
        <div className="st-page-header-content">
          <div className="st-page-title-section">
            <div className="st-page-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <div>
              <h1>Support Tickets</h1>
              <p className="st-page-subtitle">Report issues, request features, and ask questions</p>
            </div>
          </div>
          <div className="st-page-actions">
            {canWrite && (
              <button
                className="st-btn-primary"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                <span>New Ticket</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="st-stats-section">
          <div className="st-stats-grid">
            <StatCard
              title="Total Tickets"
              value={stats.total}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              }
              color="#6366f1"
            />
            <StatCard
              title="Open"
              value={stats.open || 0}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              }
              color="#22c55e"
            />
            <StatCard
              title="In Progress"
              value={stats.inProgress || 0}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              }
              color="#f59e0b"
            />
            <StatCard
              title="Resolved"
              value={stats.resolved || 0}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              }
              color="#3b82f6"
            />
          </div>
        </div>
      )}

      {/* Controls Section */}
      <div className="st-controls-section">
        <div className="st-controls-row">
          {/* Mobile Filter Toggle */}
          <button
            className="st-mobile-filter-toggle"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            <span>Filters</span>
          </button>

          {/* Filters */}
          <div className={`st-filters-container ${showMobileFilters ? 'show' : ''}`}>
            <div className="st-search-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="st-search-input"
              />
            </div>
            <div className="st-filter-group">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="st-filter-select"
              >
                <option value="all">All Status</option>
                {TICKET_STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="st-filter-select"
              >
                <option value="all">All Types</option>
                {TICKET_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="st-filter-select"
              >
                <option value="all">All Priority</option>
                {PRIORITY_LEVELS.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="st-filter-select"
              >
                <option value="all">All Sources</option>
                <option value="internal">Internal</option>
                <option value="checkout_error">Checkout Error</option>
              </select>
              {storeOptions.length > 0 && (
                <select
                  value={filterStore}
                  onChange={(e) => setFilterStore(e.target.value)}
                  className="st-filter-select"
                >
                  <option value="all">All Stores</option>
                  {storeOptions.map(slug => (
                    <option key={slug} value={slug}>{slug} ({stats.byStore[slug]})</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="st-main-content">
        {loading ? (
          <div className="st-loading-state">
            <div className="st-loading-spinner" />
            <p>Loading tickets...</p>
          </div>
        ) : error ? (
          <div className="st-error-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <h3>Error Loading Tickets</h3>
            <p>{error}</p>
            <button onClick={fetchTickets} className="st-btn-retry">Try Again</button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="st-table-container">
              {tickets.length === 0 ? (
                <div className="st-empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    <line x1="9" y1="10" x2="15" y2="10"/>
                    <line x1="12" y1="7" x2="12" y2="13"/>
                  </svg>
                  <h3>No Tickets Found</h3>
                  <p>Create your first support ticket to get started</p>
                  {canWrite && (
                    <button className="st-btn-primary" onClick={() => setIsCreateModalOpen(true)}>
                      Create Ticket
                    </button>
                  )}
                </div>
              ) : (
                <table className="st-tickets-table">
                  <thead>
                    <tr>
                      <th>Ticket #</th>
                      <th>Subject</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Submitted</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map(ticket => (
                      <tr
                        key={ticket.id}
                        className={ticket.priority === 'High' ? 'st-high-priority-row' : ''}
                        onClick={() => handleTicketClick(ticket)}
                      >
                        <td>
                          <span className="st-ticket-number-cell">{ticket.ticket_number}</span>
                        </td>
                        <td>
                          <div className="st-subject-cell">
                            <span className="st-subject-text">{ticket.subject}</span>
                            <div className="st-subject-meta">
                              {renderSourceBadge(ticket)}
                              {ticket.attachment_count > 0 && (
                                <span className="st-attachment-count" title={`${ticket.attachment_count} attachment(s)`}>
                                  📎 {ticket.attachment_count}
                                </span>
                              )}
                              {ticket.comment_count > 0 && (
                                <span className="st-comment-count" title={`${ticket.comment_count} comment(s)`}>
                                  💬 {ticket.comment_count}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{renderTypeBadge(ticket.ticket_type)}</td>
                        <td>{renderStatusIndicator(ticket.status)}</td>
                        <td>{renderPriorityBadge(ticket.priority)}</td>
                        <td>
                          <div className="st-submitted-cell">
                            <span className="st-submitted-time">{formatRelativeTime(ticket.created_at)}</span>
                            <span className="st-submitted-by">{ticket.submitted_by_name}</span>
                          </div>
                        </td>
                        <td>
                          <button className="st-btn-view" onClick={(e) => { e.stopPropagation(); handleTicketClick(ticket); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Mobile Card List */}
            <div className="st-mobile-card-list">
              {tickets.length === 0 ? (
                <div className="st-empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <h3>No Tickets Found</h3>
                </div>
              ) : (
                tickets.map(ticket => (
                  <div
                    key={ticket.id}
                    className={`st-mobile-ticket-card ${ticket.priority === 'High' ? 'high-priority' : ''}`}
                    onClick={() => handleTicketClick(ticket)}
                  >
                    <div className="st-mobile-card-header">
                      <span className="st-ticket-number">{ticket.ticket_number}</span>
                      {renderPriorityBadge(ticket.priority)}
                    </div>
                    <div className="st-mobile-card-body">
                      <div className="st-mobile-subject">{ticket.subject}</div>
                      <div className="st-mobile-type">{renderTypeBadge(ticket.ticket_type)} {renderSourceBadge(ticket)}</div>
                    </div>
                    <div className="st-mobile-card-footer">
                      {renderStatusIndicator(ticket.status)}
                      <div className="st-mobile-meta">
                        <span className="st-mobile-time">{formatRelativeTime(ticket.created_at)}</span>
                        {ticket.attachment_count > 0 && <span>📎{ticket.attachment_count}</span>}
                        {ticket.comment_count > 0 && <span>💬{ticket.comment_count}</span>}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Ticket Modal */}
      <CreateTicketModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          handleModalSuccess();
        }}
      />

      {/* Ticket Details Modal */}
      <TicketDetailsModal
        ticket={selectedTicket}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedTicket(null);
        }}
        onSuccess={handleModalSuccess}
        canAdmin={canAdmin}
        statuses={TICKET_STATUSES}
      />
    </div>
  );
};

export default Dashboard;
